/**
 * FastActions Component for Accounting Module
 *
 * Renders fast action buttons with counter badges showing linked items count.
 * Clicking opens a panel with form + list of linked items.
 */

import React, { useState, useMemo } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type ActionType =
  | 'call'
  | 'videocall'
  | 'email'
  | 'message'
  | 'task'
  | 'event'
  | 'file'
  | 'invoice'
  | 'note';

export interface LinkedCounts {
  [key: string]: number;
}

export interface FastActionsProps {
  /** Entity ID for linking */
  entityId: string;
  /** Entity name for display */
  entityName: string;
  /** Available action types */
  actions: ActionType[];
  /** Count of linked items per action type */
  linkedCounts?: LinkedCounts;
  /** Handler when action is clicked */
  onActionClick?: (actionType: ActionType, entityId: string) => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const ACTION_CONFIG: Record<ActionType, {
  label: string;
  icon: string;
  color: string;
}> = {
  call: {
    label: 'Chiamate',
    icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    color: '#16a34a',
  },
  videocall: {
    label: 'Videochiamate',
    icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
    color: '#8b5cf6',
  },
  email: {
    label: 'Email',
    icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    color: '#3b82f6',
  },
  message: {
    label: 'Messaggi',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    color: '#22c55e',
  },
  task: {
    label: 'Task',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    color: '#f59e0b',
  },
  event: {
    label: 'Eventi',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    color: '#ec4899',
  },
  file: {
    label: 'File',
    icon: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
    color: '#6b7280',
  },
  invoice: {
    label: 'Fatture',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    color: '#0891b2',
  },
  note: {
    label: 'Note',
    icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    color: '#a855f7',
  },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function FastActions({
  entityId,
  entityName,
  actions,
  linkedCounts = {},
  onActionClick,
}: FastActionsProps) {
  return (
    <div style={styles.container}>
      {actions.map((actionType) => {
        const count = linkedCounts[actionType] || 0;
        const config = ACTION_CONFIG[actionType];

        return (
          <FastActionButton
            key={actionType}
            actionType={actionType}
            count={count}
            config={config}
            onClick={() => onActionClick?.(actionType, entityId)}
          />
        );
      })}
    </div>
  );
}

// =============================================================================
// FAST ACTION BUTTON
// =============================================================================

interface FastActionButtonProps {
  actionType: ActionType;
  count: number;
  config: typeof ACTION_CONFIG[ActionType];
  onClick: () => void;
}

function FastActionButton({ actionType, count, config, onClick }: FastActionButtonProps) {
  const [hovered, setHovered] = useState(false);
  const isLinked = count > 0;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.button,
        background: hovered ? '#f3f4f6' : 'transparent',
        color: isLinked ? config.color : '#9ca3af',
      }}
      title={`${config.label}${count > 0 ? ` (${count})` : ''}`}
    >
      {/* Counter Badge */}
      {count > 0 && (
        <span
          style={{
            ...styles.badge,
            background: config.color,
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}

      {/* Icon */}
      <svg
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={config.icon}
        />
      </svg>
    </button>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '4px',
  },
  button: {
    position: 'relative',
    padding: '6px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  badge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    minWidth: '16px',
    height: '16px',
    padding: '0 4px',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
};

// =============================================================================
// EXPORTS
// =============================================================================

export { ACTION_CONFIG };
